"use client";

import { useLanguage } from "@/app/context/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const content = {
  en: {
    badge: "Legal & Terms",
    title: "Terms & Conditions",
    subtitle:
      "By accessing and using the 18m Padi Karuppanasamy Temple website, you agree to the following terms and conditions.",
    lastUpdated: "Last Updated: June 2026",

    intro: {
      icon: "📜",
      title: "Agreement to Terms",
      body: "These Terms and Conditions govern your use of the 18m Padi Karuppanasamy Temple website, operated by Trustee Kalimuthu on behalf of the temple at Sukkampatti, Kadavoor Thaluk, Karur District, Tamil Nadu. By using this website, you confirm that you accept these terms and agree to comply with them. If you do not agree with any part of these terms, please do not use this website.",
    },

    sections: [
      {
        icon: "🌐",
        title: "1. Purpose of This Website",
        items: [
          {
            subtitle: "Devotional & Informational Platform",
            body: "This website exists solely to serve the devotee community of the 18m Padi Karuppanasamy Temple. It provides information about temple timings, pooja schedules, upcoming festivals, events, history, and contact details. It is not a commercial website and does not sell any products or services.",
          },
          {
            subtitle: "Non-Commercial Nature",
            body: "All content on this website — including text, images, and schedule information — is provided freely for the spiritual benefit of devotees. No fee is charged for accessing information on this website.",
          },
          {
            subtitle: "Accuracy of Information",
            body: "While we strive to keep all information accurate and current, temple timings, pooja schedules, and event details may change without prior notice on the website. For the most up-to-date information, please call the temple directly on 9597255401 or 9361986095.",
          },
        ],
      },
      {
        icon: "✅",
        title: "2. Acceptable Use",
        items: [
          {
            subtitle: "Permitted Uses",
            body: "You may use this website to: read about the temple and its deities, view pooja schedules and festival timings, find the temple's location and contact information, send an enquiry through the contact form, and share the website link with fellow devotees.",
          },
          {
            subtitle: "Prohibited Uses",
            body: "You must not use this website in any way that is unlawful, harmful, or disrespectful to the temple, its deities, or the devotee community. You must not attempt to gain unauthorised access to any part of the website, introduce viruses or malicious code, or use the website to send unsolicited communications.",
          },
          {
            subtitle: "Respectful Conduct",
            body: "This is a sacred space — even in its digital form. Users are expected to engage with the website's content with respect and reverence for the religious traditions, deities, and devotees it represents.",
          },
        ],
      },
      {
        icon: "©️",
        title: "3. Intellectual Property",
        items: [
          {
            subtitle: "Ownership of Content",
            body: "All content on this website — including text, layout, images, and design — is the property of the 18m Padi Karuppanasamy Temple administration. Unauthorised reproduction, distribution, or commercial use of any content from this website is prohibited without prior written permission from the temple trustee.",
          },
          {
            subtitle: "Religious Imagery",
            body: "All deity images, temple photographs, and religious symbols displayed on this website are sacred in nature. They must not be used, reproduced, or altered for any purpose that is disrespectful, commercial, or contrary to Hindu religious traditions.",
          },
          {
            subtitle: "Third-Party Content",
            body: "Some images used on this website may be sourced from third-party providers. Their respective copyrights apply. We acknowledge these rights and use such content only for devotional and informational purposes.",
          },
        ],
      },
      {
        icon: "💰",
        title: "4. Donations",
        items: [
          {
            subtitle: "Voluntary Contributions",
            body: "Any donations made to the 18m Padi Karuppanasamy Temple are entirely voluntary. Donations are used for the maintenance of the temple premises, daily pooja requirements, festival celebrations, and the welfare of temple staff.",
          },
          {
            subtitle: "No Online Payment",
            body: "This website does not currently process online payments or donations. To make a donation, please contact the temple trustee directly by phone. All donation arrangements are made in person or via direct communication with the temple administration.",
          },
          {
            subtitle: "No Refunds",
            body: "As donations are voluntary religious offerings, they are generally non-refundable. If you have a concern about a donation made directly to the temple, please contact the trustee at 9597255401.",
          },
        ],
      },
      {
        icon: "🔗",
        title: "5. Third-Party Links & Services",
        items: [
          {
            subtitle: "Google Maps",
            body: "This website embeds Google Maps to assist devotees in locating the temple. Use of the map is subject to Google's Terms of Service and Privacy Policy. The temple is not responsible for any data collected by Google through this embed.",
          },
          {
            subtitle: "WhatsApp",
            body: "The WhatsApp contact button on this website directs you to an external WhatsApp chat. Interactions through WhatsApp are governed by Meta's Terms of Service. The temple is not responsible for the conduct of the WhatsApp platform.",
          },
          {
            subtitle: "External Links",
            body: "This website may contain links to external websites for reference. The temple does not endorse, control, or take responsibility for the content of any external websites linked to from this site.",
          },
        ],
      },
      {
        icon: "⚠️",
        title: "6. Limitation of Liability",
        items: [
          {
            subtitle: "No Warranties",
            body: "This website is provided on an 'as is' basis without any warranties of any kind — express or implied. The temple administration makes no representations about the accuracy, completeness, or suitability of the information on this website for any particular purpose.",
          },
          {
            subtitle: "Service Interruptions",
            body: "We do not guarantee that this website will be available at all times. The website may be temporarily unavailable due to maintenance, technical issues, or circumstances beyond our control. The temple is not liable for any inconvenience caused by website downtime.",
          },
          {
            subtitle: "Spiritual Representations",
            body: "The information about religious practices, pooja benefits, festival significance, and deity histories on this website is provided for informational and devotional purposes only. The temple makes no guarantees regarding spiritual outcomes.",
          },
        ],
      },
      {
        icon: "🔒",
        title: "7. Privacy",
        items: [
          {
            subtitle: "Privacy Policy",
            body: "Your use of this website is also governed by our Privacy Policy, which is incorporated into these Terms and Conditions by reference. Please read our Privacy Policy to understand how we collect, use, and protect your information.",
          },
        ],
      },
      {
        icon: "🔄",
        title: "8. Changes to These Terms",
        items: [
          {
            subtitle: "Right to Modify",
            body: "The temple administration reserves the right to revise these Terms and Conditions at any time. Changes will be posted on this page with an updated 'Last Updated' date. Your continued use of the website after changes are posted constitutes acceptance of the revised terms. We recommend checking this page periodically.",
          },
        ],
      },
      {
        icon: "⚖️",
        title: "9. Governing Law",
        items: [
          {
            subtitle: "Jurisdiction",
            body: "These Terms and Conditions are governed by and construed in accordance with the laws of India. Any disputes arising from your use of this website shall be subject to the exclusive jurisdiction of the courts in Karur District, Tamil Nadu.",
          },
        ],
      },
      {
        icon: "📞",
        title: "10. Contact",
        items: [
          {
            subtitle: "Questions About These Terms",
            body: "If you have any questions about these Terms and Conditions or wish to seek clarification on any matter, please contact the temple administration directly. We are committed to addressing all concerns with transparency and care.",
          },
        ],
      },
    ],

    contact: {
      label: "Temple Administration",
      trustee: "Trustee: Kalimuthu",
      address: "Kadavoor Thaluk, Kaniyalampatti Road, Sukkampatti, Karur, Tamil Nadu",
      phones: ["9597255401", "9361986095"],
      hours: "Open All 7 Days · Morning 4 AM – 12 PM · Evening 4 PM – 7 PM",
    },

    note: {
      icon: "🙏",
      title: "A Note from the Temple",
      body: "This website is a humble digital extension of the sacred 18m Padi Karuppanasamy Temple. These terms exist not to create barriers, but to ensure this space remains a respectful, trustworthy, and devotion-filled platform for all who seek the blessings of Karuppanasamy.",
    },
  },

  ta: {
    badge: "சட்ட & விதிமுறைகள்",
    title: "விதிமுறைகள் & நிபந்தனைகள்",
    subtitle:
      "18ம் படி கருப்பண்ணசாமி கோவில் இணையதளத்தை அணுகி பயன்படுத்துவதன் மூலம், நீங்கள் பின்வரும் விதிமுறைகள் மற்றும் நிபந்தனைகளுக்கு ஒப்புக்கொள்கிறீர்கள்.",
    lastUpdated: "கடைசியாக புதுப்பிக்கப்பட்டது: ஜூன் 2026",

    intro: {
      icon: "📜",
      title: "விதிமுறைகளுக்கான ஒப்பந்தம்",
      body: "இந்த விதிமுறைகள் மற்றும் நிபந்தனைகள் கடவூர் தாலுகா, கரூர் மாவட்டம், தமிழ்நாட்டில் உள்ள சுக்கம்பட்டி கோவிலின் சார்பாக நம்பகர் களிமுத்து இயக்கும் 18ம் படி கருப்பண்ணசாமி கோவில் இணையதளத்தின் உங்கள் பயன்பாட்டை ஆளுகின்றன. இந்த இணையதளத்தை பயன்படுத்துவதன் மூலம், நீங்கள் இந்த விதிமுறைகளை ஏற்று அவற்றை கடைப்பிடிக்க ஒப்புக்கொள்கிறீர்கள்.",
    },

    sections: [
      {
        icon: "🌐",
        title: "1. இந்த இணையதளத்தின் நோக்கம்",
        items: [
          {
            subtitle: "பக்தி & தகவல் தளம்",
            body: "இந்த இணையதளம் 18ம் படி கருப்பண்ணசாமி கோவிலின் பக்தர் சமுதாயத்திற்கு சேவை செய்ய மட்டுமே உள்ளது. கோவில் நேரங்கள், பூஜை அட்டவணைகள், வரவிருக்கும் விழாக்கள், நிகழ்வுகள், வரலாறு மற்றும் தொடர்பு விவரங்கள் பற்றிய தகவல்களை வழங்குகிறது.",
          },
          {
            subtitle: "வணிகமற்ற தன்மை",
            body: "இந்த இணையதளத்தில் உள்ள அனைத்து உள்ளடக்கமும் — உரை, படங்கள் மற்றும் அட்டவணை தகவல் உட்பட — பக்தர்களின் ஆன்மீக நலனுக்காக இலவசமாக வழங்கப்படுகிறது.",
          },
          {
            subtitle: "தகவலின் துல்லியம்",
            body: "கோவில் நேரங்கள், பூஜை அட்டவணைகள் மற்றும் நிகழ்வு விவரங்கள் முன்னறிவிப்பு இல்லாமல் மாறலாம். மிக தற்போதைய தகவலுக்கு, நேரடியாக 9597255401 அல்லது 9361986095 என்ற எண்ணில் அழைக்கவும்.",
          },
        ],
      },
      {
        icon: "✅",
        title: "2. ஏற்புடைய பயன்பாடு",
        items: [
          {
            subtitle: "அனுமதிக்கப்பட்ட பயன்பாடுகள்",
            body: "கோவில் மற்றும் அதன் தெய்வங்களைப் பற்றி படிக்க, பூஜை அட்டவணைகள் மற்றும் திருவிழா நேரங்களை பார்க்க, கோவிலின் இருப்பிடம் மற்றும் தொடர்பு தகவல்களை கண்டுபிடிக்க, தொடர்பு படிவம் மூலம் விசாரணை அனுப்ப இந்த இணையதளத்தை நீங்கள் பயன்படுத்தலாம்.",
          },
          {
            subtitle: "தடைசெய்யப்பட்ட பயன்பாடுகள்",
            body: "கோவில், அதன் தெய்வங்கள் அல்லது பக்தர் சமுதாயத்திற்கு சட்டவிரோதமான, தீங்கான அல்லது மரியாதையற்ற எந்த வழியிலும் இந்த இணையதளத்தை நீங்கள் பயன்படுத்தக்கூடாது.",
          },
          {
            subtitle: "மரியாதையான நடத்தை",
            body: "இது ஒரு புனித இடம் — அதன் டிஜிட்டல் வடிவத்திலும் கூட. பயனர்கள் இணையதளத்தின் உள்ளடக்கத்துடன் மத மரபுகள், தெய்வங்கள் மற்றும் பக்தர்களுக்கு மரியாதையுடன் ஈடுபடுவார்கள் என்று எதிர்பார்க்கப்படுகிறது.",
          },
        ],
      },
      {
        icon: "©️",
        title: "3. அறிவுசார் சொத்துரிமை",
        items: [
          {
            subtitle: "உள்ளடக்கத்தின் உரிமை",
            body: "இந்த இணையதளத்தில் உள்ள அனைத்து உள்ளடக்கமும் 18ம் படி கருப்பண்ணசாமி கோவில் நிர்வாகத்தின் சொத்தாகும். கோவில் நம்பகரின் முன் எழுத்துப்பூர்வ அனுமதியின்றி இணையதளத்திலிருந்து எந்த உள்ளடக்கத்தையும் அங்கீகரிக்கப்படாத இனப்பெருக்கம், விநியோகம் அல்லது வணிக பயன்பாடு தடைசெய்யப்பட்டுள்ளது.",
          },
          {
            subtitle: "மதப் படங்கள்",
            body: "இந்த இணையதளத்தில் காட்டப்படும் அனைத்து தெய்வ படங்கள், கோவில் புகைப்படங்கள் மற்றும் மத சின்னங்கள் தன்மையில் புனிதமானவை. அவை மரியாதையற்ற, வணிக அல்லது இந்து மத மரபுகளுக்கு முரணான எந்த நோக்கத்திற்காகவும் பயன்படுத்தப்படக்கூடாது.",
          },
          {
            subtitle: "மூன்றாம் தரப்பு உள்ளடக்கம்",
            body: "இந்த இணையதளத்தில் பயன்படுத்தப்படும் சில படங்கள் மூன்றாம் தரப்பு வழங்குநர்களிடமிருந்து பெறப்பட்டிருக்கலாம். அவற்றின் தனித்தனி பதிப்புரிமைகள் பொருந்தும்.",
          },
        ],
      },
      {
        icon: "💰",
        title: "4. நன்கொடைகள்",
        items: [
          {
            subtitle: "தன்னார்வ பங்களிப்புகள்",
            body: "18ம் படி கருப்பண்ணசாமி கோவிலுக்கு வழங்கப்படும் எந்த நன்கொடையும் முழுமையாக தன்னார்வமானது. கோவில் வளாகத்தின் பராமரிப்பு, தினசரி பூஜை தேவைகள், திருவிழா கொண்டாட்டங்கள் மற்றும் கோவில் ஊழியர்களின் நலனுக்காக நன்கொடைகள் பயன்படுத்தப்படுகின்றன.",
          },
          {
            subtitle: "ஆன்லைன் கட்டணம் இல்லை",
            body: "இந்த இணையதளம் தற்போது ஆன்லைன் கட்டணங்கள் அல்லது நன்கொடைகளை செயலாக்காது. நன்கொடை செய்ய, நேரடியாக தொலைபேசியில் கோவில் நம்பகரை தொடர்பு கொள்ளுங்கள்.",
          },
          {
            subtitle: "திரும்பப்பெறுதல் இல்லை",
            body: "நன்கொடைகள் தன்னார்வ மத சமர்ப்பிப்புகள் என்பதால், அவை பொதுவாக திரும்பப்பெற முடியாதவை. நேரடியாக கோவிலுக்கு வழங்கிய நன்கொடை பற்றி கவலை இருந்தால், 9597255401 என்ற எண்ணில் நம்பகரை தொடர்பு கொள்ளுங்கள்.",
          },
        ],
      },
      {
        icon: "🔗",
        title: "5. மூன்றாம் தரப்பு இணைப்புகள் & சேவைகள்",
        items: [
          {
            subtitle: "கூகிள் மேப்ஸ்",
            body: "பக்தர்கள் கோவிலை கண்டுபிடிக்க உதவ இந்த இணையதளம் கூகிள் மேப்ஸை உட்பொதிக்கிறது. கூகிளின் சேவை விதிமுறைகள் பொருந்தும். கூகிள் சேகரிக்கும் தரவுக்கு கோவில் பொறுப்பல்ல.",
          },
          {
            subtitle: "வாட்ஸ்அப்",
            body: "இந்த இணையதளத்தில் உள்ள வாட்ஸ்அப் தொடர்பு பொத்தான் உங்களை வெளிப்புற வாட்ஸ்அப் அரட்டைக்கு இயக்குகிறது. வாட்ஸ்அப் மூலம் தொடர்புகள் மெட்டாவின் சேவை விதிமுறைகளால் நிர்வகிக்கப்படுகின்றன.",
          },
          {
            subtitle: "வெளிப்புற இணைப்புகள்",
            body: "இந்த இணையதளம் குறிப்புக்காக வெளிப்புற இணையதளங்களுக்கான இணைப்புகளை கொண்டிருக்கலாம். இந்த தளத்திலிருந்து இணைக்கப்பட்ட எந்த வெளிப்புற இணையதளங்களின் உள்ளடக்கத்திற்கும் கோவில் பொறுப்பல்ல.",
          },
        ],
      },
      {
        icon: "⚠️",
        title: "6. பொறுப்பின் வரம்பு",
        items: [
          {
            subtitle: "உத்தரவாதங்கள் இல்லை",
            body: "இந்த இணையதளம் எந்த வகையான உத்தரவாதங்களும் இல்லாமல் 'இருக்கும் நிலையில்' வழங்கப்படுகிறது. இந்த இணையதளத்தில் உள்ள தகவல்களின் துல்லியம், முழுமை அல்லது எந்த குறிப்பிட்ட நோக்கத்திற்கும் பொருத்தம் பற்றி கோவில் நிர்வாகம் எந்த அறிவிப்பும் செய்வதில்லை.",
          },
          {
            subtitle: "சேவை இடைத்தடைகள்",
            body: "இந்த இணையதளம் எல்லா நேரங்களிலும் கிடைக்கும் என்று நாங்கள் உத்தரவாதம் அளிப்பதில்லை. பராமரிப்பு, தொழில்நுட்ப சிக்கல்கள் காரணமாக தளம் தற்காலிகமாக கிடைக்காமல் போகலாம்.",
          },
          {
            subtitle: "ஆன்மீக அறிவிப்புகள்",
            body: "இந்த இணையதளத்தில் மத நடைமுறைகள், பூஜை பலன்கள் மற்றும் தெய்வ வரலாறுகள் பற்றிய தகவல்கள் தகவல் மற்றும் பக்தி நோக்கங்களுக்காக மட்டுமே வழங்கப்படுகின்றன.",
          },
        ],
      },
      {
        icon: "🔒",
        title: "7. தனியுரிமை",
        items: [
          {
            subtitle: "தனியுரிமைக் கொள்கை",
            body: "இந்த இணையதளத்தின் உங்கள் பயன்பாடு எங்கள் தனியுரிமைக் கொள்கையாலும் நிர்வகிக்கப்படுகிறது, இது குறிப்பு மூலம் இந்த விதிமுறைகள் மற்றும் நிபந்தனைகளில் சேர்க்கப்பட்டுள்ளது.",
          },
        ],
      },
      {
        icon: "🔄",
        title: "8. இந்த விதிமுறைகளில் மாற்றங்கள்",
        items: [
          {
            subtitle: "மாற்றியமைக்கும் உரிமை",
            body: "கோவில் நிர்வாகம் எந்த நேரத்திலும் இந்த விதிமுறைகள் மற்றும் நிபந்தனைகளை திருத்திக்கொள்ள உரிமை ஒதுக்கிவைக்கிறது. மாற்றங்கள் புதுப்பிக்கப்பட்ட 'கடைசியாக புதுப்பிக்கப்பட்டது' தேதியுடன் இந்த பக்கத்தில் இடுகையிடப்படும்.",
          },
        ],
      },
      {
        icon: "⚖️",
        title: "9. நிர்வாக சட்டம்",
        items: [
          {
            subtitle: "அதிகார வரம்பு",
            body: "இந்த விதிமுறைகள் மற்றும் நிபந்தனைகள் இந்தியாவின் சட்டங்களுக்கு இணங்க நிர்வகிக்கப்படுகின்றன மற்றும் விளக்கப்படுகின்றன. இந்த இணையதளத்தின் உங்கள் பயன்பாட்டிலிருந்து எழும் எந்த சர்ச்சைகளும் தமிழ்நாட்டில் கரூர் மாவட்ட நீதிமன்றங்களின் பிரத்யேக அதிகார வரம்பிற்கு உட்பட்டிருக்கும்.",
          },
        ],
      },
      {
        icon: "📞",
        title: "10. தொடர்பு",
        items: [
          {
            subtitle: "இந்த விதிமுறைகள் பற்றிய கேள்விகள்",
            body: "இந்த விதிமுறைகள் மற்றும் நிபந்தனைகள் பற்றி ஏதேனும் கேள்விகள் இருந்தால் அல்லது எந்த விஷயத்திலும் தெளிவு கோர விரும்பினால், நேரடியாக கோவில் நிர்வாகத்தை தொடர்பு கொள்ளுங்கள்.",
          },
        ],
      },
    ],

    contact: {
      label: "கோவில் நிர்வாகம்",
      trustee: "நம்பகர்: களிமுத்து",
      address: "கடவூர் தாலுகா, கணியாலம்பட்டி ரோடு, சுக்கம்பட்டி, கரூர், தமிழ்நாடு",
      phones: ["9597255401", "9361986095"],
      hours: "வாரம் 7 நாட்களும் திறந்திருக்கும் · காலை 4 AM – 12 PM · மாலை 4 PM – 7 PM",
    },

    note: {
      icon: "🙏",
      title: "கோவிலிலிருந்து ஒரு குறிப்பு",
      body: "இந்த இணையதளம் புனிதமான 18ம் படி கருப்பண்ணசாமி கோவிலின் தாழ்மையான டிஜிட்டல் நீட்சியாகும். இந்த விதிமுறைகள் தடைகளை உருவாக்க அல்ல, மாறாக கருப்பண்ணசாமியின் ஆசீர்வாதம் தேடும் அனைவருக்கும் இந்த இடத்தை மரியாதையான, நம்பகமான மற்றும் பக்தி நிறைந்த தளமாக வைத்திருக்க இருக்கின்றன.",
    },
  },
};

export default function TermsAndConditionsPage() {
  const { language } = useLanguage();
  const t = content[language as "en" | "ta"] ?? content.en;

  return (
    <main className="bg-neutral-950 text-neutral-100 min-h-screen font-sans selection:bg-amber-900 selection:text-amber-100">

      {/* ── HERO ── */}
      <section className="relative py-28 flex flex-col items-center text-center px-4 overflow-hidden border-b border-neutral-900">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/20 via-neutral-950 to-neutral-950 z-0" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-600/6 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute top-20 left-16 w-36 h-36 bg-orange-700/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 right-16 w-48 h-48 bg-amber-600/6 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 space-y-5 max-w-2xl mx-auto mt-10">
          <Badge
            variant="outline"
            className="text-amber-400 border-amber-500/30 bg-amber-950/30 backdrop-blur-md px-5 py-1.5 text-xs font-medium tracking-widest uppercase"
          >
            {t.badge}
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold font-serif leading-tight">
            <span className="bg-gradient-to-r from-amber-200 via-yellow-400 to-orange-400 bg-clip-text text-transparent">
              {t.title}
            </span>
          </h1>
          <p className="text-neutral-400 text-base leading-relaxed max-w-xl mx-auto">
            {t.subtitle}
          </p>
          <div className="inline-flex items-center gap-2 bg-neutral-900/60 border border-neutral-800 rounded-full px-5 py-2 text-xs text-neutral-500 backdrop-blur-sm">
            🗓️ {t.lastUpdated}
          </div>
        </div>

        {/* Quick-nav pills */}
      
      </section>

      {/* ── INTRO ── */}
      <section className="py-14 border-b border-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <Card className="bg-gradient-to-br from-amber-950/20 to-neutral-900 border-amber-800/30">
            <CardContent className="p-8 md:p-10 flex gap-6 items-start">
              <div className="text-4xl shrink-0">{t.intro.icon}</div>
              <div className="space-y-3">
                <h2 className="text-xl font-serif font-bold text-amber-400">
                  {t.intro.title}
                </h2>
                <p className="text-neutral-400 leading-relaxed text-sm md:text-base">
                  {t.intro.body}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── SECTIONS ── */}
      <section className="py-14">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl space-y-5">
          {t.sections.map((section, si) => (
            <Card
              key={si}
              className="bg-neutral-900/50 border-neutral-800 hover:border-neutral-700 transition-colors overflow-hidden"
            >
              <CardContent className="p-0">
                {/* Section header */}
                <div className="flex items-center gap-4 px-8 py-6 border-b border-neutral-800 bg-neutral-900/40">
                  <div className="w-12 h-12 rounded-2xl bg-amber-950/40 border border-amber-900/30 flex items-center justify-center text-2xl shrink-0">
                    {section.icon}
                  </div>
                  <h2 className="text-lg md:text-xl font-serif font-bold text-amber-400">
                    {section.title}
                  </h2>
                </div>

                {/* Items */}
                <div className="divide-y divide-neutral-800/50">
                  {section.items.map((item, ii) => (
                    <div key={ii} className="px-8 py-6 space-y-2">
                      <h3 className="text-sm font-semibold text-neutral-200 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                        {item.subtitle}
                      </h3>
                      <p className="text-neutral-500 text-sm leading-relaxed pl-3.5">
                        {item.body}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ── NOTE FROM TEMPLE ── */}
      <section className="py-10 border-t border-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <Card className="bg-gradient-to-br from-amber-950/15 to-neutral-900 border-amber-800/25">
            <CardContent className="p-8 text-center space-y-4">
              <div className="text-4xl">{t.note.icon}</div>
              <h3 className="text-lg font-serif font-bold text-amber-400">
                {t.note.title}
              </h3>
              <p className="text-neutral-500 text-sm leading-relaxed max-w-2xl mx-auto">
                {t.note.body}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── CONTACT STRIP ── */}
      <section className="py-14 border-t border-neutral-900 bg-gradient-to-t from-amber-950/10 to-neutral-950">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-2">
              <p className="text-xs text-amber-500/70 uppercase tracking-widest font-medium">
                {t.contact.label}
              </p>
              <p className="text-lg font-serif font-bold text-neutral-200">
                18m Padi Karuppanasamy Temple
              </p>
              <p className="text-neutral-500 text-sm">{t.contact.trustee}</p>
              <p className="text-neutral-600 text-xs mt-1">
                📍 {t.contact.address}
              </p>
              <Separator className="bg-neutral-800 my-3" />
              <div className="flex items-center gap-2 text-xs text-neutral-600">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                {t.contact.hours}
              </div>
            </div>

            <div className="space-y-3">
              {t.contact.phones.map((num, i) => (
                <a key={i} href={`tel:${num}`} className="block">
                  <div className="flex items-center justify-between bg-neutral-900/60 border border-neutral-800 hover:border-amber-700/40 rounded-xl px-5 py-4 group transition-colors">
                    <span className="text-amber-400 font-mono tracking-wider text-sm">
                      +91 {num}
                    </span>
                    <span className="text-neutral-600 group-hover:text-amber-500 transition-colors">
                      📞
                    </span>
                  </div>
                </a>
              ))}

              <a
                href="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3383.410248085217!2d78.21896917427173!3d10.792580358897139!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3baa3f1041b0d51f%3A0x850ce1d3c4748c71!2sSri%2018m%20padi%20karupanasamy%20temple!5e1!3m2!1sen!2sin!4v1780481684628!5m2!1sen!2sin"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <div className="flex items-center justify-between bg-neutral-900/40 border border-neutral-800 hover:border-amber-700/30 rounded-xl px-5 py-4 group transition-colors">
                  <span className="text-neutral-400 text-sm">
                    {language === "ta"
                      ? "கூகிள் மேப்ஸில் திற"
                      : "Open in Google Maps"}
                  </span>
                  <span className="text-neutral-600 group-hover:text-amber-500 transition-colors">
                    📍
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}